/**
 * AppHeader — Header zone for the App Shell layout (Phase 8: persistent actions).
 *
 * Header: logo (left), NotificationCenter + ProcessingPill + Record + Upload + Activity Bar toggle (right).
 * - NotificationCenter (bell) shows dropdown with all notifications history
 * - ProcessingPill shows inline status of what's currently processing
 * - Record and Upload are persistent icon-only buttons that open the Activity Bar
 *
 * See: docs/features/infrastructure/activity-bar-redesign.md#phase-8a-restore-record--upload-to-appheader
 * See: docs/features/infrastructure/notification-system.md
 */
import React from "react";
import { Link } from "react-router-dom";
import { PanelBottom, Mic, FileUp, FileStack } from "lucide-react";
import Tooltip from "@mui/material/Tooltip";
import logo from "../../assets/featherglass-logo.png";
import logoWhite from "../../assets/featherglass-logo-white.png";
import NotificationCenter from "../notifications/NotificationCenter";
import ProcessingPill from "./ProcessingPill";
import HeaderUserMenu from "./UserMenu";
import { useAppShellContext } from "../../contexts/AppShellContext";
import { useUnifiedNotifications } from "../../contexts/UnifiedNotificationContext";
import { useThemeMode } from "../../contexts/ThemeContext";
import "../../css/NotificationCenter.css";

const AppHeader = ({
    isStreaming = false,
    isActivityBarOpen = false,
    onToggleActivityBar,
    onNavigateToDocument,
    onNavigateToPatient,
    hideToggle = false,
    userData,
}) => {
    const { openActivityBar } = useAppShellContext();
    const { activeJobs } = useUnifiedNotifications();
    const { isDarkMode } = useThemeMode();
    const toggleLabel = isActivityBarOpen ? "Close activity bar" : "Open activity bar";

    return (
        <header className="app-shell-header">
            {/* Left: Logo */}
            <div className="app-shell-header-left">
                <Link to="/dashboard">
                    <img
                        src={isDarkMode ? logoWhite : logo}
                        alt="Logo"
                        className="app-shell-logo"
                    />
                </Link>
            </div>

            {/* Right: ProcessingPill + NotificationCenter + Record + Upload + Activity Bar toggle */}
            <div className="app-shell-header-right">
                {/* Inline indicator for active processing */}
                <ProcessingPill jobs={activeJobs} />

                {/* Bell dropdown with full notification history */}
                <NotificationCenter
                    onNavigateToDocument={onNavigateToDocument}
                    onNavigateToPatient={onNavigateToPatient}
                />

                {/* Persistent Record + Upload buttons (Phase 8a) */}
                <Tooltip title="Record Session" arrow>
                    <span
                        className={`app-shell-header-action${isStreaming ? " recording" : ""}`}
                        onClick={() => openActivityBar("record", { explicit: true })}
                        role="button"
                        aria-label="Record Session"
                    >
                        <Mic className="app-shell-header-icon" size={25} />
                        {/* Phase 6: Red circle removed — Recording Status Bar in ActivityBarHeader provides visual indication */}
                    </span>
                </Tooltip>

                <Tooltip title="Upload Document" arrow>
                    <span
                        className="app-shell-header-action"
                        onClick={() => openActivityBar("upload", { explicit: true })}
                        role="button"
                        aria-label="Upload Document"
                    >
                        <FileUp className="app-shell-header-icon" size={25} />
                    </span>
                </Tooltip>

                {/* Generate Summary — see docs/features/document-system/summary-generation.md */}
                <Tooltip title="Generate Summary" arrow>
                    <span
                        className="app-shell-header-action"
                        onClick={() => openActivityBar("summary", { explicit: true })}
                        role="button"
                        aria-label="Generate Summary"
                    >
                        <FileStack className="app-shell-header-icon" size={25} />
                    </span>
                </Tooltip>

                {!hideToggle && (
                    <Tooltip title={toggleLabel} arrow>
                        <span
                            className={`app-shell-header-action app-shell-header-toggle${isActivityBarOpen ? " active" : ""}`}
                            onClick={onToggleActivityBar}
                            role="button"
                            aria-label={toggleLabel}
                            aria-expanded={isActivityBarOpen}
                        >
                            <PanelBottom className="app-shell-header-icon" size={25} />
                        </span>
                    </Tooltip>
                )}

                {/* Mobile-only hamburger menu — the sidebar (and its user menu) is hidden < 768px */}
                <HeaderUserMenu userData={userData} />
            </div>
        </header>
    );
};

export default AppHeader;
